import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { loadYT } from "@/lib/youtube";
import api from "@/lib/api";
import * as wakeLock from "@/lib/wakeLock";
import { useI18n } from "@/context/I18n";
import VideoStage from "@/components/music/VideoStage";
import {
  indexAfterMove,
  indexAfterRemoval,
  isPermanentError,
  pickNextIndex,
  pickNextPlayable,
  stallVerdict,
} from "@/lib/playback";

const MusicPlayerContext = createContext(null);

const SESSION_KEY = "sonlyo.player";
/** Videos this session has already seen refuse to play, by provider id. */
const UNPLAYABLE_KEY = "sonlyo.unplayable";

/** How long the queue may grow while an artist radio keeps extending it. */
const MAX_QUEUE = 300;
/** How much of what has already played is kept behind the current track. */
const KEEP_BEHIND = 20;

/**
 * Provider ids known not to play, remembered across refreshes.
 *
 * A video whose owner blocked embedding will block it again tomorrow, and
 * rediscovering that costs the listener a gap in the music every time. The
 * list is capped because it is a cache, not a record.
 */
function restoreUnplayable() {
  try {
    const raw = JSON.parse(localStorage.getItem(UNPLAYABLE_KEY) || "[]");
    return new Set(Array.isArray(raw) ? raw.filter((id) => typeof id === "string").slice(-300) : []);
  } catch {
    return new Set();
  }
}

/** Last queue and position, so the player is still there after a refresh. */
function restoreSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!Array.isArray(data.queue) || data.queue.length === 0) return null;
    if (typeof data.index !== "number" || data.index < 0 || data.index >= data.queue.length) return null;
    return data;
  } catch {
    return null;
  }
}

export function MusicPlayerProvider({ children }) {
  const { t } = useI18n();
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  const mountRef = useRef(null);
  const mountBRef = useRef(null);
  // playerRef is always whichever player is the audio source; spareRef is the
  // other one, sitting silent with the next track cued. They trade places on
  // every change of track, so every other call site can keep reading
  // playerRef.current without knowing two exist.
  const playerRef = useRef(null);
  const spareRef = useRef(null);
  const spareCuedRef = useRef(null);
  const makeSpareRef = useRef(null);
  const activeKeyRef = useRef("a");
  // Whether the listener wants audio, as opposed to the browser having
  // paused us in the background.
  const wantsPlayRef = useRef(false);
  const prevRef = useRef(() => {});
  const nextRef = useRef(() => {});
  const intervalRef = useRef(null);
  const watchdogRef = useRef(null);
  const pendingRef = useRef(null);
  const volumeRef = useRef(80);

  // Everything the watchdog needs to know about the track being attempted
  // right now. `id` counts up on every load, and is what stops one track from
  // being skipped twice when an error, an ENDED event and the watchdog all
  // decide to move the queue on at the same moment.
  const attemptRef = useRef({
    id: 0,
    providerId: null,
    startedAt: 0,
    lastPosition: 0,
    lastProgressAt: 0,
    hasPlayed: false,
    nudges: 0,
    reloaded: false,
  });
  const advancedFromRef = useRef(-1);
  const unplayableRef = useRef(restoreUnplayable());
  const reportedRef = useRef(new Set());
  // Set for the one load that must not start playing by itself.
  const suppressAutoplayRef = useRef(false);
  // Whether the listener has asked for music in this session, as opposed to
  // the app having tried to pick a restored session back up on its own.
  const userIntentRef = useRef(false);

  const saved = restoreSession();
  const restoringRef = useRef(saved ? saved.position || 0 : 0);
  const needsCueRef = useRef(!!saved);
  // Whether the previous session was playing when it went away.
  const resumePlaybackRef = useRef(!!(saved && saved.playing));
  const hasPlayedRef = useRef(false);

  const [queue, setQueue] = useState(saved ? saved.queue : []);
  const [index, setIndex] = useState(saved ? saved.index : -1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [current, setCurrent] = useState(saved ? saved.position || 0 : 0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [ready, setReady] = useState(false);
  const [spareReady, setSpareReady] = useState(false);
  const [activeKey, setActiveKey] = useState("a");
  const [radio, setRadio] = useState(saved ? !!saved.radio : false);
  const [shuffle, setShuffle] = useState(localStorage.getItem("sonlyo_shuffle") === "1");
  // "off" plays through, "all" restarts the queue, "one" repeats this track.
  const [repeat, setRepeat] = useState(localStorage.getItem("sonlyo_repeat") || "off");
  const shuffleRef = useRef(shuffle);
  const repeatRef = useRef(repeat);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { repeatRef.current = repeat; }, [repeat]);

  // Video mode. The picture is off by default — the app is audio-first — and
  // the player sits as a 1x1 iframe off-screen. Turning it on only restyles
  // the host: the iframe is never rebuilt, so the track never stops. See
  // VideoStage for how YouTube's own chrome is kept out of sight.
  const [videoMode, setVideoMode] = useState(localStorage.getItem("sonlyo_video") === "1");
  const toggleVideoMode = useCallback(() => {
    setVideoMode((v) => {
      localStorage.setItem("sonlyo_video", v ? "0" : "1");
      return !v;
    });
  }, []);

  const toggleShuffle = useCallback(() => {
    setShuffle((v) => {
      localStorage.setItem("sonlyo_shuffle", v ? "0" : "1");
      return !v;
    });
  }, []);

  const cycleRepeat = useCallback(() => {
    setRepeat((v) => {
      const next = v === "off" ? "all" : v === "all" ? "one" : "off";
      localStorage.setItem("sonlyo_repeat", next);
      return next;
    });
  }, []);
  const [crossfade, setCrossfadeState] = useState(localStorage.getItem("sonlyo_crossfade") === "1");
  const crossfadeRef = useRef(crossfade);
  const fadeInStartRef = useRef(0);
  useEffect(() => { crossfadeRef.current = crossfade; }, [crossfade]);
  const setCrossfade = useCallback((v) => {
    localStorage.setItem("sonlyo_crossfade", v ? "1" : "0");
    setCrossfadeState(v);
    const p = playerRef.current;
    if (!v && p && p.setVolume) p.setVolume(volumeRef.current);
  }, []);

  /**
   * Picks the music back up after a refresh.
   *
   * Autoplay with sound is gated by the browser: Chrome allows it once a site
   * has enough listening history, Safari generally does not. If it is refused
   * the track stays cued at the saved position, which is the fallback anyway,
   * so this costs nothing to attempt.
   */
  const tryContinue = useCallback((player) => {
    if (!resumePlaybackRef.current || !player || !player.playVideo) return;
    resumePlaybackRef.current = false;
    wantsPlayRef.current = true;
    setTimeout(() => {
      try {
        player.playVideo();
      } catch {
        /* blocked; the listener presses play and it starts where it left off */
      }
    }, 350);
  }, []);

  const currentTrack = index >= 0 && index < queue.length ? queue[index] : null;

  // refs kept in sync so the (static) onStateChange handler reads fresh values
  const queueRef = useRef(queue);
  const indexRef = useRef(index);
  const radioRef = useRef(radio);
  const extendingRef = useRef(false);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { indexRef.current = index; }, [index]);
  useEffect(() => { radioRef.current = radio; }, [radio]);

  // Bumped to make the loader run again for an index that has not changed —
  // repeating a track, or retrying one that refused to start.
  const [loadNonce, setLoadNonce] = useState(0);
  const replay = useCallback(() => setLoadNonce((n) => n + 1), []);

  /** Is there any point pointing the player at the track in this slot? */
  const isPlayableAt = useCallback((i) => {
    const track = queueRef.current[i];
    if (!track) return false;
    // No source yet is not the same as no source: it gets looked up on demand.
    if (!track.provider_id) return true;
    return !unplayableRef.current.has(track.provider_id);
  }, []);

  /**
   * Write a video off, and tell the API so the next listener does not meet it.
   *
   * The server can usually find another recording of the same song, so a
   * refusal here is repairable rather than permanent for everybody. The report
   * is sent once per session per track and its outcome is not waited on: the
   * queue has to keep moving either way.
   */
  const markUnplayable = useCallback((providerId, trackId) => {
    if (!providerId) return;
    unplayableRef.current.add(providerId);
    try {
      localStorage.setItem(
        UNPLAYABLE_KEY,
        JSON.stringify([...unplayableRef.current].slice(-300)),
      );
    } catch {
      /* storage unavailable */
    }
    if (trackId && !reportedRef.current.has(trackId)) {
      reportedRef.current.add(trackId);
      api.post(`/music/track/${trackId}/unplayable`, { provider_id: providerId }).catch(() => {});
    }
  }, []);

  /** Come to a stop without leaving the watchdog trying to revive anything. */
  const stopPlayback = useCallback(() => {
    wantsPlayRef.current = false;
    setIsPlaying(false);
  }, []);

  /**
   * Keep an artist radio going once the queue behind it runs out.
   *
   * The exclude list is what the queue has already played, and on a small
   * catalogue that eventually matches everything the artist has, at which
   * point the API can only answer with nothing. Asking again without the list
   * is the difference between the radio looping and the radio dying, and a
   * loop is unambiguously the better of the two.
   */
  const extendRadio = useCallback(async () => {
    if (extendingRef.current) return false;
    const cur = queueRef.current[indexRef.current];
    if (!cur || !cur.artist) return false;
    extendingRef.current = true;
    try {
      const ask = (exclude) =>
        api.post("/music/radio", { artist: cur.artist, exclude_ids: exclude, limit: 5 });

      let data = null;
      try {
        ({ data } = await ask(queueRef.current.map((track) => track.provider_id).filter(Boolean)));
      } catch {
        data = null;
      }
      if (!data?.tracks?.length) {
        // Second pass: only rule out what is playing right now.
        try {
          ({ data } = await ask([cur.provider_id].filter(Boolean)));
        } catch {
          data = null;
        }
      }

      const fresh = (data?.tracks || []).filter(
        (track) => track && !unplayableRef.current.has(track.provider_id),
      );
      if (fresh.length) {
        // A radio that loops rather than dying can run for hours, and every
        // extension used to grow the queue for good. Tracks well behind the
        // one playing are dropped to keep it bounded, leaving enough history
        // for the previous-track button to still mean something.
        const q = queueRef.current;
        const overflow = q.length + fresh.length - MAX_QUEUE;
        const drop = overflow > 0
          ? Math.min(overflow, Math.max(0, indexRef.current - KEEP_BEHIND))
          : 0;
        // Land on the join rather than on "one more than now", so a skip that
        // arrived while the request was in flight is not undone.
        setQueue([...q.slice(drop), ...fresh]);
        setIndex(q.length - drop);
        return true;
      }
      return false;
    } finally {
      extendingRef.current = false;
    }
  }, []);

  /**
   * Move the queue on by itself: the track ended, or gave up, or never began.
   *
   * Guarded by the id of the attempt it was called about, because ENDED, the
   * error handler and the watchdog can all reach this conclusion within the
   * same second. Without the guard the queue jumps two or three tracks at
   * once, which reads as songs being missed out.
   */
  const advance = useCallback((reason = "ended") => {
    const attempt = attemptRef.current;
    if (advancedFromRef.current === attempt.id) return;
    advancedFromRef.current = attempt.id;

    const size = queueRef.current.length;
    if (size === 0) { stopPlayback(); return; }

    // Repeating one track is not advancing, so it is settled before anything
    // else — except when the track is the reason we are here, in which case
    // repeating it would loop on silence forever.
    if (repeatRef.current === "one" && reason !== "unplayable" && reason !== "stalled") {
      replay();
      return;
    }

    const target = pickNextPlayable({
      index: indexRef.current,
      size,
      shuffle: shuffleRef.current,
      repeat: repeatRef.current,
      isPlayable: isPlayableAt,
    });

    if (target !== null) {
      if (target === indexRef.current) replay();
      else setIndex(target);
      return;
    }

    if (radioRef.current) {
      extendRadio().then((grew) => {
        if (!grew) {
          stopPlayback();
          toast.info(tRef.current("radio_exhausted"));
        }
      });
      return;
    }

    stopPlayback();
  }, [extendRadio, isPlayableAt, replay, stopPlayback]);

  const advanceRef = useRef(advance);
  useEffect(() => { advanceRef.current = advance; }, [advance]);

  /**
   * The listener pressing skip.
   *
   * Unlike `advance` this ignores repeat-one — asking for the next track and
   * being given the same one again is not what the button says — and it is
   * never held back by the guard, because a person pressing a button twice
   * means it twice.
   */
  const next = useCallback(() => {
    const size = queueRef.current.length;
    if (size === 0) return;
    userIntentRef.current = true;
    const target = pickNextPlayable({
      index: indexRef.current,
      size,
      shuffle: shuffleRef.current,
      // Repeat-one would answer "this one again", which is not what the
      // button says. Every other mode keeps its own end-of-queue behaviour.
      repeat: repeatRef.current === "one" ? "all" : repeatRef.current,
      isPlayable: isPlayableAt,
    });
    if (target === null) {
      if (radioRef.current) extendRadio();
      return;
    }
    if (target === indexRef.current) replay();
    else setIndex(target);
  }, [extendRadio, isPlayableAt, replay]);

  const endedRef = useRef(() => {});
  useEffect(() => { endedRef.current = () => advanceRef.current("ended"); }, []);

  // Create both players as soon as the app mounts. The iframe API script and
  // the player handshake are what make the first press of play feel slow, so
  // they happen while the listener is still browsing.
  //
  // There are two of them because one player has to tear the current video
  // down before it can fetch the next, which measured at close to seven
  // seconds of silence between tracks. The spare carries the upcoming track
  // already buffered, so a change of track becomes a swap.
  useEffect(() => {
    let cancelled = false;

    // The API writes width/height attributes on the iframe, so the size has to
    // be taken back in CSS for the crop to work. Pointer events stay off for
    // good: nothing in the picture is clickable, which is what keeps a
    // listener from landing on youtube.com.
    const dress = (player) => {
      try {
        const frame = player.getIframe();
        frame.style.width = "100%";
        frame.style.height = "100%";
        frame.style.border = "0";
        frame.style.display = "block";
        frame.style.pointerEvents = "none";
      } catch {}
    };

    const build = (YT, mount, key) =>
      new YT.Player(mount, {
        height: "1",
        width: "1",
        // No YouTube UI at all: no controls, no annotations, no related
        // videos. Transport is the app's own player bar, in video mode as
        // much as out of it.
        playerVars: { controls: 0, disablekb: 1, modestbranding: 1, rel: 0, iv_load_policy: 3, playsinline: 1, origin: window.location.origin },
        events: {
          onReady: (e) => {
            dress(e.target);
            if (key !== activeKeyRef.current) {
              // The spare is never heard. It only buffers.
              try { e.target.mute(); e.target.setVolume(0); } catch {}
              // The preloader bails out when it finds a spare that is not
              // ready to be cued yet. Without this it had no way of knowing it
              // had become ready, so the first change of track never got the
              // preload it was built for and fell back to the slow path.
              setSpareReady(true);
              return;
            }
            setReady(true);
            e.target.setVolume(volumeRef.current);
            if (pendingRef.current) {
              if (needsCueRef.current) {
                needsCueRef.current = false;
                e.target.cueVideoById({ videoId: pendingRef.current, startSeconds: restoringRef.current });
                tryContinue(e.target);
              } else {
                e.target.loadVideoById(pendingRef.current);
              }
              pendingRef.current = null;
            }
          },
          onStateChange: (e) => {
            // Only the player that is currently the audio source drives the
            // app. The spare reports CUED and BUFFERING as it preloads, and
            // none of that is the listener's playback.
            if (e.target !== playerRef.current) return;
            const S = window.YT.PlayerState;
            if (e.data === S.PLAYING) {
              setIsPlaying(true);
              wantsPlayRef.current = true;
              setDuration(e.target.getDuration());
              const attempt = attemptRef.current;
              attempt.hasPlayed = true;
              attempt.nudges = 0;
              attempt.lastProgressAt = Date.now();
              // Belt and braces for the restored position: if the player
              // started from the top despite being cued with startSeconds,
              // put it back where the listener left off. Only ever once.
              if (!hasPlayedRef.current && restoringRef.current > 1) {
                const target = restoringRef.current;
                restoringRef.current = 0;
                if (e.target.getCurrentTime() < target - 2) e.target.seekTo(target, true);
              }
              hasPlayedRef.current = true;
            }
            else if (e.data === S.PAUSED) setIsPlaying(false);
            else if (e.data === S.ENDED) {
              setIsPlaying(false);
              if (repeatRef.current === "one" && playerRef.current?.seekTo) {
                playerRef.current.seekTo(0, true);
                playerRef.current.playVideo();
              } else {
                endedRef.current();
              }
            }
          },
          /**
           * A video that will not play at all.
           *
           * Nothing used to listen for this, and it is the reason the queue
           * would stop between songs and stay there: YouTube reports the
           * refusal here and then does nothing further — no PLAYING, no
           * ENDED — so a player waiting on a state change waits forever. The
           * catalogue is built from YouTube search results, and a fair number
           * of those turn out to be blocked from embedding, taken down, or
           * unavailable in this country, so this fires in normal use.
           */
          onError: (e) => {
            const code = Number(e.data);
            const failing = e.target === playerRef.current;
            if (!failing) {
              // The spare choked on a preload. Forget it, so the swap does not
              // promote a player that cannot play, and let the track be
              // loaded normally when it comes round.
              if (spareCuedRef.current) {
                if (isPermanentError(code)) markUnplayable(spareCuedRef.current, null);
                spareCuedRef.current = null;
              }
              return;
            }

            const attempt = attemptRef.current;
            const track = queueRef.current[indexRef.current];
            // The HTML5 player failing is often nothing to do with the video;
            // one reload of the same id usually clears it.
            if (!isPermanentError(code) && !attempt.reloaded) {
              attempt.reloaded = true;
              attempt.startedAt = Date.now();
              attempt.lastProgressAt = Date.now();
              try { e.target.loadVideoById(attempt.providerId); } catch {}
              return;
            }

            markUnplayable(attempt.providerId || track?.provider_id, track?.id);
            setIsPlaying(false);
            toast.error(tRef.current("track_unavailable"), {
              description: track?.title || undefined,
            });
            advanceRef.current("unplayable");
          },
        },
      });

    loadYT().then((YT) => {
      if (cancelled || !mountRef.current || playerRef.current) return;
      playerRef.current = build(YT, mountRef.current, "a");
      // The spare is built on first use rather than here. Two YouTube iframes
      // racing on boot measurably slowed the first render, and nothing needs
      // preloading until there is a queue to look ahead in.
      makeSpareRef.current = () => {
        if (spareRef.current || !mountBRef.current) return null;
        spareRef.current = build(YT, mountBRef.current, "b");
        return spareRef.current;
      };
    });
    return () => { cancelled = true; if (intervalRef.current) clearInterval(intervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Songs catalogued from a record arrive without anything to play.
   *
   * The provider named every track on the album, which is enough to list
   * them; finding audio for each one up front would have held the whole
   * import to a crawl. So the search happens here, the first time somebody
   * asks for the song, and the answer is kept — the next listener plays it
   * straight away.
   */
  const [resolving, setResolving] = useState(false);
  // Tracks this session has already failed to find audio for. Without it a
  // queue of unresolvable songs asks the API for each one again on every pass.
  const unresolvableRef = useRef(new Set());
  const resolvingIdRef = useRef(null);

  const resolveSource = useCallback(async (track) => {
    if (!track || track.provider_id) return track?.provider_id ?? null;
    // One lookup at a time for a given track: the effect can run twice for
    // the same one, and the second call would only duplicate the first.
    // `undefined` says "ask again later", which is not the same answer as the
    // `null` that means no recording exists — treating the two alike would
    // write a track off while the search that could have saved it was still
    // running.
    if (resolvingIdRef.current === track.id) return undefined;
    resolvingIdRef.current = track.id;
    setResolving(true);
    try {
      const { data } = await api.post(`/music/track/${track.id}/source`);
      if (data.provider_id) {
        setQueue((q) => q.map((t) => (t.id === track.id ? { ...t, provider_id: data.provider_id } : t)));
        return data.provider_id;
      }
      return null;
    } catch {
      return null;
    } finally {
      resolvingIdRef.current = null;
      setResolving(false);
    }
  }, []);

  // load track on index change
  useEffect(() => {
    if (!currentTrack) return;

    // A fresh attempt at a track. Everything the watchdog measures runs from
    // here, and — importantly — the guard that stops one track being skipped
    // twice is released, so that a run which itself decides to skip is not
    // silently refused by the skip that brought us here.
    attemptRef.current = {
      id: attemptRef.current.id + 1,
      providerId: currentTrack.provider_id || null,
      startedAt: Date.now(),
      lastPosition: -1,
      lastProgressAt: Date.now(),
      hasPlayed: false,
      nudges: 0,
      reloaded: false,
    };

    // Nothing to load yet: find a source, and let the change of provider_id
    // bring this effect back round.
    //
    // The lookup can come back empty — the song is on the record but no
    // recording of it can be found — and until now that left the queue parked
    // on a track that would never play. It has to keep moving instead.
    if (!currentTrack.provider_id) {
      if (unresolvableRef.current.has(currentTrack.id)) {
        advanceRef.current("unplayable");
        return;
      }
      let dropped = false;
      resolveSource(currentTrack).then((providerId) => {
        if (providerId !== null || dropped) return;
        unresolvableRef.current.add(currentTrack.id);
        if (queueRef.current[indexRef.current]?.id !== currentTrack.id) return;
        toast.error(tRef.current("track_unavailable"), { description: currentTrack.title || undefined });
        advanceRef.current("unplayable");
      });
      return () => { dropped = true; };
    }

    // Already known not to play. Pointing the player at it would only
    // reproduce the same silence, so it is stepped over before it starts.
    if (unplayableRef.current.has(currentTrack.provider_id)) {
      advanceRef.current("unplayable");
      return;
    }

    // The old track's length must not be read as this one's, or the watchdog
    // would decide a track that has only just started is already at its end.
    setDuration(0);

    fadeInStartRef.current = Date.now();

    // The spare is already holding this video, buffered: promote it instead
    // of fetching the same thing again. Restoring a session is excluded —
    // that path needs a cue at a saved offset, which no preload anticipates.
    if (!needsCueRef.current && spareRef.current && spareCuedRef.current === currentTrack.provider_id) {
      const outgoing = playerRef.current;
      playerRef.current = spareRef.current;
      spareRef.current = outgoing;
      spareCuedRef.current = null;
      activeKeyRef.current = activeKeyRef.current === "a" ? "b" : "a";
      setActiveKey(activeKeyRef.current);
      try {
        outgoing.stopVideo();
        outgoing.mute();
      } catch {}
      const incoming = playerRef.current;
      try {
        incoming.unMute();
        incoming.setVolume(crossfadeRef.current ? 0 : volumeRef.current);
        incoming.playVideo();
      } catch {}
      setCurrent(0);
      return;
    }

    const p = playerRef.current;
    if (crossfadeRef.current && p && p.setVolume) p.setVolume(0);
    if (p && p.loadVideoById) {
      if (needsCueRef.current) {
        needsCueRef.current = false;
        p.cueVideoById({ videoId: currentTrack.provider_id, startSeconds: restoringRef.current });
        setCurrent(restoringRef.current);
        tryContinue(p);
      } else if (suppressAutoplayRef.current) {
        // Cue rather than load: the track is put in place without being
        // started, which is what a stopped queue is supposed to look like.
        suppressAutoplayRef.current = false;
        p.cueVideoById(currentTrack.provider_id);
        setCurrent(0);
      } else {
        p.loadVideoById(currentTrack.provider_id);
        setCurrent(0);
      }
    } else {
      pendingRef.current = currentTrack.provider_id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, currentTrack?.provider_id, loadNonce]);

  /**
   * Buffer the next track on the spare player while this one plays.
   *
   * Shuffle and radio are deliberately left out: neither knows what comes
   * next until it gets there, so there is nothing to preload and the swap
   * simply does not fire — those fall back to loading on demand as before.
   */
  useEffect(() => {
    if (!ready) return;
    if (shuffle || repeat === "one") return;
    const upcoming = queue[index + 1] || (repeat === "all" ? queue[0] : null);
    if (!upcoming || !upcoming.provider_id) return;
    // No sense holding a video open that has already refused to play.
    if (unplayableRef.current.has(upcoming.provider_id)) return;
    if (spareCuedRef.current === upcoming.provider_id) return;
    if (!spareRef.current && makeSpareRef.current) makeSpareRef.current();
    const spare = spareRef.current;
    // A player built a moment ago is not ready to be cued yet. `spareReady`
    // is what brings this effect back once it is, instead of the preload
    // being silently skipped until the track after next.
    if (!spare || !spare.cueVideoById) return;
    try {
      spare.mute();
      spare.cueVideoById(upcoming.provider_id);
      spareCuedRef.current = upcoming.provider_id;
    } catch {}
  }, [index, queue, ready, spareReady, shuffle, repeat]);

  // record listening history
  useEffect(() => {
    if (currentTrack?.id) {
      api.post("/music/history", { track_id: currentTrack.id })
        .then(() => window.dispatchEvent(new Event("history-changed")))
        .catch(() => {});
    }
  }, [currentTrack?.id]);

  // progress ticker (+ crossfade volume ramp)
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        const p = playerRef.current;
        if (p && p.getCurrentTime) {
          const cur = p.getCurrentTime();
          const dur = p.getDuration();
          setCurrent(cur); setDuration(dur);
          if (crossfadeRef.current && dur && p.setVolume) {
            const FADE = 4, target = volumeRef.current;
            const fin = (Date.now() - fadeInStartRef.current) / 1000;
            let v = target;
            if (fin < FADE) v = Math.min(v, target * Math.max(0.05, fin / FADE));
            if (dur - cur < FADE) v = Math.min(v, target * Math.max(0, (dur - cur) / FADE));
            p.setVolume(Math.round(v));
          }
        }
      }, 250);
    } else if (intervalRef.current) clearInterval(intervalRef.current);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [isPlaying]);

  /**
   * The watchdog: the thing that makes sure the queue is never simply stuck.
   *
   * The IFrame player has several ways of going quiet that are neither an
   * error nor an ending, and each one used to park the app on a track that
   * would never finish and therefore never hand over to the next: it can stay
   * UNSTARTED after a load, buffer indefinitely, be paused out from under us,
   * or run to the final second and never emit ENDED. To the listener all four
   * look the same — the music stopped and the queue did not move — so this
   * watches the player's own clock rather than trusting its events, and
   * escalates: ask it to play, then load the video again, then give up on it.
   *
   * It only ever acts while the listener wants sound. A deliberate pause is
   * left completely alone.
   */
  useEffect(() => {
    if (!ready || !currentTrack) return undefined;

    watchdogRef.current = setInterval(() => {
      const p = playerRef.current;
      const attempt = attemptRef.current;
      if (!p || !p.getPlayerState || !attempt.providerId) return;
      if (!wantsPlayRef.current) return;
      // On a restored session the app asks the player to continue by itself,
      // and a browser is entitled to refuse: the track then sits cued and
      // silent until the listener presses play, which is the intended
      // fallback and not a fault. Reviving or skipping it here would fight
      // the browser's own policy, so nothing is judged until either something
      // has actually played or the listener has asked for it.
      if (!hasPlayedRef.current && !userIntentRef.current) return;
      // A resolve or a radio call is in flight; give it room to finish.
      if (extendingRef.current || resolvingIdRef.current) return;

      let state;
      let position;
      try {
        state = p.getPlayerState();
        position = p.getCurrentTime() || 0;
      } catch {
        return;
      }

      const now = Date.now();
      // Half a second of tolerance: the reported time drifts a little even
      // when playback is perfectly healthy.
      if (Math.abs(position - attempt.lastPosition) > 0.5) {
        attempt.lastPosition = position;
        attempt.lastProgressAt = now;
      }

      const verdict = stallVerdict({
        state,
        position,
        duration: (() => { try { return p.getDuration() || 0; } catch { return 0; } })(),
        sinceProgressMs: now - attempt.lastProgressAt,
        sinceAttemptMs: now - attempt.startedAt,
        hasPlayed: attempt.hasPlayed,
        nudges: attempt.nudges,
        reloaded: attempt.reloaded,
        wantsPlay: wantsPlayRef.current,
        visible: document.visibilityState === "visible",
      });

      if (verdict === "wait") return;

      if (verdict === "nudge") {
        attempt.nudges += 1;
        attempt.lastProgressAt = now;
        try { p.playVideo(); } catch {}
        return;
      }

      if (verdict === "reload") {
        attempt.reloaded = true;
        attempt.nudges = 0;
        attempt.startedAt = now;
        attempt.lastProgressAt = now;
        attempt.lastPosition = -1;
        try {
          p.loadVideoById(attempt.providerId);
          p.playVideo();
        } catch {}
        return;
      }

      if (verdict === "ended") { advanceRef.current("ended"); return; }

      // "advance": this track has had every chance. It is deliberately not
      // written off as unplayable — a stall is as easily a bad minute of
      // connection as a bad video, and blacklisting on that evidence would
      // quietly delete working songs from the catalogue. Only an outright
      // refusal from YouTube earns that.
      //
      // With no connection at all there is nothing to move on to, so the
      // queue waits instead of eating itself while the listener is offline.
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      advanceRef.current("stalled");
    }, 1000);

    return () => {
      if (watchdogRef.current) clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, currentTrack?.id, loadNonce]);

  const playQueue = useCallback((tracks, startIndex = 0) => {
    if (!tracks || tracks.length === 0) return;
    userIntentRef.current = true;
    setQueue(tracks);
    setIndex(startIndex);
  }, []);

  const playTrack = useCallback((track) => playQueue([track], 0), [playQueue]);

  const addNext = useCallback((track) => {
    if (indexRef.current < 0) { playQueue([track], 0); return; }
    setQueue((q) => { const nq = [...q]; nq.splice(indexRef.current + 1, 0, track); return nq; });
  }, [playQueue]);

  const addToQueue = useCallback((track) => {
    if (indexRef.current < 0) { playQueue([track], 0); return; }
    setQueue((q) => [...q, track]);
  }, [playQueue]);

  const startArtistRadio = useCallback((artistName, tracks) => {
    setRadio(true);
    if (tracks && tracks.length) playQueue(tracks, 0);
  }, [playQueue]);

  const togglePlay = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    // An explicit pause is intent; the visibility handler must not undo it.
    userIntentRef.current = true;
    wantsPlayRef.current = !isPlaying;
    if (isPlaying) p.pauseVideo(); else p.playVideo();
  }, [isPlaying]);

  const prev = useCallback(() => {
    userIntentRef.current = true;
    const p = playerRef.current;
    if (p && p.getCurrentTime && p.getCurrentTime() > 3) { p.seekTo(0, true); return; }
    setIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const seek = useCallback((ratio) => {
    const p = playerRef.current;
    if (p && duration) { p.seekTo(ratio * duration, true); setCurrent(ratio * duration); }
  }, [duration]);

  const changeVolume = useCallback((v) => {
    setVolume(v); volumeRef.current = v;
    const p = playerRef.current;
    if (p && p.setVolume) p.setVolume(v);
  }, []);

  /**
   * Media Session: puts the track on the lock screen and the notification
   * shade, and tells the OS this tab is an audio session, which is what stops
   * a phone from tearing it down the moment the screen goes off.
   *
   * Playback runs through the YouTube iframe player, so a mobile browser can
   * still suspend it when the tab is backgrounded; the visibility handler
   * below picks playback back up when the listener returns. A browser tab
   * cannot hold audio the way a native app does.
   */
  useEffect(() => {
    if (!("mediaSession" in navigator) || !currentTrack) return;
    const artwork = currentTrack.thumbnail
      ? [96, 192, 512].map((size) => ({
          src: currentTrack.thumbnail,
          sizes: `${size}x${size}`,
          type: "image/jpeg",
        }))
      : [];
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: currentTrack.title || "",
      artist: currentTrack.artist || "",
      album: currentTrack.album_name || "SONLYO",
      artwork,
    });
  }, [currentTrack]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
  }, [isPlaying]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const handlers = {
      // Pausing from the lock screen is the listener's intent just as much as
      // pressing pause in the app is, and the watchdog reads that intent to
      // decide whether silence is a fault. Without recording it here the
      // watchdog would take a lock-screen pause for a stall and start the
      // music again a couple of seconds later.
      play: () => { wantsPlayRef.current = true; playerRef.current?.playVideo?.(); },
      pause: () => { wantsPlayRef.current = false; playerRef.current?.pauseVideo?.(); },
      previoustrack: () => prevRef.current(),
      nexttrack: () => nextRef.current(),
      seekto: (details) => {
        if (details.fastSeek && playerRef.current?.seekTo) {
          playerRef.current.seekTo(details.seekTime, true);
        } else if (playerRef.current?.seekTo) {
          playerRef.current.seekTo(details.seekTime, true);
        }
      },
    };
    for (const [action, handler] of Object.entries(handlers)) {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        /* action unsupported on this browser */
      }
    }
    return () => {
      for (const action of Object.keys(handlers)) {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  /**
   * Resume when the listener comes back.
   *
   * Playback runs through the YouTube iframe player, and YouTube pauses that
   * player whenever a mobile browser backgrounds the page: it is their policy,
   * not something the page can opt out of. So the best available behaviour is
   * to pick straight back up the moment the app is in front again, without the
   * listener having to press play.
   *
   * Several signals are watched because they do not all fire on every
   * platform: visibilitychange is the reliable one on Android, pageshow covers
   * a restore from the back/forward cache, and focus catches a desktop window
   * being raised.
   */
  useEffect(() => {
    let attempts = 0;

    const resume = () => {
      if (document.visibilityState !== "visible") return;
      const p = playerRef.current;
      if (!p || !p.getPlayerState || !wantsPlayRef.current) return;

      const S = window.YT && window.YT.PlayerState;
      if (!S) return;

      const state = p.getPlayerState();
      // PAUSED is the usual one; CUED and UNSTARTED show up when the player
      // was torn down and rebuilt while the app was away.
      if (state === S.PAUSED || state === S.CUED || state === S.UNSTARTED) {
        p.playVideo();

        // Mobile browsers sometimes ignore the first call while the page is
        // still settling, so try again briefly rather than leaving it silent.
        if (attempts < 3) {
          attempts += 1;
          setTimeout(() => {
            if (
              document.visibilityState === "visible" &&
              wantsPlayRef.current &&
              p.getPlayerState &&
              p.getPlayerState() !== S.PLAYING
            ) {
              p.playVideo();
            }
          }, 400 * attempts);
        }
      } else if (state === S.PLAYING) {
        attempts = 0;
      }
    };

    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("focus", resume);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("focus", resume);
    };
  }, []);

  // Keep the session so a refresh does not empty the player, and so playback
  // picks up where it left off. One key, always overwritten: this is the
  // current track, not a history of everything listened to.
  //
  // The player reports a time of 0 until it actually starts, so a naive save
  // on mount would immediately erase the position we just restored. Nothing is
  // written until either playback has begun or the position has moved.
  //
  // Saving used to be debounced by 800ms against `current`, which meant it
  // never ran: the progress ticker moves `current` every 250ms, so each tick
  // cancelled the pending write and rescheduled it. The only thing ever
  // written was the state at mount — position zero, not playing — and that is
  // what a refresh restored, which looked like the track starting over.
  //
  // So the snapshot is kept in a ref and written on its own clock instead,
  // plus once more on the way out, which is the write that matters when the
  // listener refreshes a second after skipping.
  const sessionRef = useRef({ queue, index, current, radio });
  useEffect(() => {
    sessionRef.current = { queue, index, current, radio };
  }, [queue, index, current, radio]);

  useEffect(() => {
    const write = () => {
      const snap = sessionRef.current;
      if (snap.index < 0 || snap.queue.length === 0) return;

      const position = Math.floor(snap.current);
      // Do not stamp a zero over a position that was restored but has not
      // been played back yet.
      if (position === 0 && restoringRef.current > 0 && !hasPlayedRef.current) return;

      try {
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            queue: snap.queue.slice(0, 200),
            index: snap.index,
            position,
            radio: snap.radio,
            playing: wantsPlayRef.current,
          }),
        );
      } catch {
        /* quota or private mode */
      }
    };

    const id = setInterval(write, 3000);
    // pagehide fires on a refresh and on a mobile browser dropping the page,
    // where unload is not guaranteed to run at all.
    window.addEventListener("pagehide", write);
    document.addEventListener("visibilitychange", write);
    return () => {
      clearInterval(id);
      window.removeEventListener("pagehide", write);
      document.removeEventListener("visibilitychange", write);
      write();
    };
  }, []);

  const [keepAwake, setKeepAwakeState] = useState(wakeLock.isEnabled());

  const setKeepAwake = useCallback((value) => {
    wakeLock.setEnabled(value);
    setKeepAwakeState(value);
    if (!value) wakeLock.release();
  }, []);

  /**
   * Hold the screen on while the music plays, when the listener asks for it.
   *
   * YouTube stops the embedded player as soon as a mobile browser backgrounds
   * the page, and that rule cannot be turned off. Keeping the screen on means
   * never going to the background, which is the one thing that actually keeps
   * playback alive on a phone. The browser drops the lock whenever the page is
   * hidden, so it is taken again on the way back.
   */
  useEffect(() => {
    if (!keepAwake || !wakeLock.isSupported()) {
      wakeLock.release();
      return undefined;
    }

    const sync = () => {
      if (isPlaying && document.visibilityState === "visible") wakeLock.acquire();
      else if (!isPlaying) wakeLock.release();
    };
    sync();

    // The system can reclaim the lock at any time; take it again while the
    // music is still going, or the setting silently stops working.
    wakeLock.setOnLost(() => {
      setTimeout(sync, 300);
    });

    document.addEventListener("visibilitychange", sync);
    return () => {
      wakeLock.setOnLost(null);
      document.removeEventListener("visibilitychange", sync);
    };
  }, [keepAwake, isPlaying]);

  // The player is built at 1x1, and YouTube lays its video surface out from
  // the size it was handed rather than from the box the iframe ends up in.
  // Restyling the host is therefore not enough on its own: setSize is what
  // makes it lay out again, so the picture fills the frame instead of
  // rendering into a single pixel.
  useEffect(() => {
    if (!ready) return;
    const observers = [];
    for (const player of [playerRef.current, spareRef.current]) {
      if (!player || !player.setSize || !player.getIframe) continue;
      const frame = player.getIframe();
      const box = frame && frame.parentElement;
      if (!box) continue;
      const apply = () => {
        const r = box.getBoundingClientRect();
        if (r.width < 2 || r.height < 2) return;
        try {
          player.setSize(Math.round(r.width), Math.round(r.height));
          frame.style.width = "100%";
          frame.style.height = "100%";
        } catch {}
      };
      apply();
      if (typeof ResizeObserver === "undefined") continue;
      const ro = new ResizeObserver(apply);
      ro.observe(box);
      observers.push(ro);
    }
    return () => observers.forEach((ro) => ro.disconnect());
  }, [videoMode, ready, activeKey]);

  useEffect(() => () => wakeLock.release(), []);

  useEffect(() => { prevRef.current = prev; nextRef.current = next; }, [prev, next]);

  const isCurrent = useCallback((track) => currentTrack && track && currentTrack.id === track.id, [currentTrack]);
  const jumpTo = useCallback((i) => {
    userIntentRef.current = true;
    if (i === indexRef.current) replay();
    else setIndex(i);
  }, [replay]);

  /**
   * Reordering and removal have to carry the playing index with them.
   *
   * The index is a position in the list, so anything that shifts the list
   * shifts what "now playing" points at. Dragging a track from below the
   * current one to above it, or deleting something already played, used to
   * leave the index where it was and jump the music to a different song.
   */
  const moveInQueue = useCallback((from, to) => {
    setQueue((q) => {
      if (to < 0 || to >= q.length || from < 0 || from >= q.length || from === to) return q;
      const nq = [...q]; const [m] = nq.splice(from, 1); nq.splice(to, 0, m); return nq;
    });
    setIndex((i) => indexAfterMove(i, from, to));
  }, []);

  const removeFromQueue = useCallback((i) => {
    const size = queueRef.current.length;
    if (i < 0 || i >= size) return;
    setQueue((q) => { const nq = [...q]; nq.splice(i, 1); return nq; });

    const playing = indexRef.current;
    if (i !== playing) { setIndex((cur) => indexAfterRemoval(cur, i)); return; }

    // The track being removed is the one playing. The next one slides into
    // this slot, so the index stays put — unless the queue ended here, in
    // which case there is nowhere left to slide from.
    if (playing < size - 1) { replay(); return; }
    const wrapped = pickNextIndex({ index: playing, size: size - 1, repeat: repeatRef.current });
    if (wrapped !== null) { setIndex(wrapped); return; }
    // That was the end of the queue. Stop, and leave the previous track
    // loaded but silent rather than starting it unasked.
    stopPlayback();
    try { playerRef.current?.pauseVideo?.(); } catch {}
    suppressAutoplayRef.current = true;
    setIndex(size > 1 ? size - 2 : -1);
  }, [replay, stopPlayback]);

  return (
    <MusicPlayerContext.Provider
      value={{
        currentTrack, queue, index, isPlaying, current, duration, volume, ready, radio,
        shuffle, toggleShuffle, repeat, cycleRepeat,
        keepAwake, setKeepAwake, keepAwakeSupported: wakeLock.isSupported(),
        playQueue, playTrack, togglePlay, next, prev, seek, changeVolume, isCurrent,
        jumpTo, moveInQueue, removeFromQueue, addNext, addToQueue, startArtistRadio, setRadio,
        crossfade, setCrossfade,
        videoMode, toggleVideoMode, resolving,
      }}
    >
      <VideoStage
        mountARef={mountRef}
        mountBRef={mountBRef}
        activeKey={activeKey}
        visible={videoMode}
        poster={currentTrack && currentTrack.thumbnail}
        title={currentTrack && currentTrack.title}
        playing={isPlaying}
        current={current}
        duration={duration}
        onClose={toggleVideoMode}
      />
      {children}
    </MusicPlayerContext.Provider>
  );
}

export const useMusicPlayer = () => useContext(MusicPlayerContext);
